import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Globe, Storefront, TrendUp, Megaphone, Compass } from "@phosphor-icons/react";

const ABOUT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b2f61d21-026a-4c77-a648-aeefa1ae1c0a/images/0cb45972dca897e5ac7a8e6f241b68f51a65da9f2a2e4c80e7a74186aa6789b3.png";

// 3 operational bases — surfaced inline inside the About hero, not as a separate section.
const FOOTPRINT = [
  { code: "KR", country: "South Korea", role: "HQ · Innovation", accent: "#FF6B9E" },
  { code: "AU", country: "Australia", role: "Asia-Pacific gateway", accent: "#A78BFA" },
  { code: "IN", country: "India", role: "Emerging markets", accent: "#38BDF8" },
];

const APPROACH = [
  { title: "Korean Innovation", body: "Designed and engineered with Korea's globally recognized R&D and formulation strengths.", Icon: Compass },
  { title: "Premium yet Accessible", body: "Positioned at the meeting point of luxury craftsmanship and everyday relevance.", Icon: Storefront },
  { title: "Trend-Native", body: "Built around fast-moving global consumer behavior — not legacy export catalogues.", Icon: TrendUp },
  { title: "Social-Driven Branding", body: "Brands designed for digital, social and culture-first audiences.", Icon: Megaphone },
  { title: "Cross-Cultural Reach", body: "Scaling across Asia-Pacific markets through localised commercial models.", Icon: Globe },
];

const DEFAULT_NARRATIVE =
  "Hangang Global is a modern Korean lifestyle company specializing in premium beauty, wellness, and beverage products designed for the next generation of global consumers. With operational roots across South Korea, Australia and India, we connect Korean innovation with emerging international markets through a culturally driven and globally connected business model.";

export default function AboutSection() {
  const [narrative, setNarrative] = useState(DEFAULT_NARRATIVE);

  useEffect(() => {
    api
      .get("/about")
      .then((r) => {
        if (r.data?.narrative) setNarrative(r.data.narrative);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="about"
      className="relative py-28 md:py-40 px-5 md:px-8 overflow-hidden"
      data-testid="about-section"
    >
      {/* Decorative ambient blobs — make the About section visually distinct */}
      <div className="absolute inset-0 pointer-events-none -z-0" aria-hidden>
        <div className="absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#FF6B9E]/10 blur-[120px]" />
        <div className="absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#A78BFA]/12 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 w-[24rem] h-[24rem] rounded-full bg-[#38BDF8]/10 blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* ── Hero band — bigger, dramatic, with footprint integrated ──────────── */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* LEFT — image with sticker accents and footprint markers */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 order-2 lg:order-1"
          >
            <div className="relative">
              {/* Offset bg accent — neo-brutalist hint */}
              <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-[#FF6B9E]/30 via-[#A78BFA]/20 to-[#38BDF8]/30 blur-2xl" />
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-[0_20px_70px_-15px_rgba(15,23,42,0.25)]">
                <img src={ABOUT_IMG} alt="Seoul x Hangang Global" className="w-full h-full object-cover" draggable={false} />
                <div className="absolute top-4 left-4 sticker bg-white/95">Seoul · HQ</div>
                <div className="absolute bottom-4 right-4 sticker bg-[#0F172A] text-white border-[#0F172A]">Est. 2026</div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — eyebrow, hero headline, narrative, footprint chips */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 order-1 lg:order-2"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-10 h-px bg-[#0F172A]" />
              <span className="label-uppercase text-[#0F172A]/80">03 · About Hangang</span>
            </div>

            <h2
              className="font-display font-light tracking-[-0.035em] leading-[0.95] text-[2.6rem] sm:text-[3.6rem] lg:text-[5.2rem] xl:text-[6rem]"
              data-testid="about-headline"
            >
              <span className="block">Born in</span>
              <span className="block">
                <span className="font-serif italic font-normal">South</span>{" "}
                <span className="font-medium">Korea.</span>
              </span>
              <span className="block text-gradient-pink font-semibold">Connected to the world.</span>
            </h2>

            <p
              className="mt-9 text-[15px] md:text-[17px] text-slate-700 leading-[1.85] max-w-2xl"
              data-testid="about-narrative"
            >
              {narrative}
            </p>

            {/* ── Footprint chips — replaces the old standalone section ────────── */}
            <div
              className="mt-10 flex flex-wrap items-stretch gap-3 sm:gap-4"
              data-testid="about-footprint"
            >
              {FOOTPRINT.map((f, i) => (
                <motion.div
                  key={f.code}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex items-center gap-3 rounded-full pl-2 pr-5 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 hover:border-slate-300 hover:-translate-y-0.5 transition-all duration-300 shadow-sm"
                >
                  <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full font-display font-semibold text-[12px] text-white"
                    style={{ background: f.accent }}
                  >
                    {f.code}
                  </span>
                  <div className="leading-tight">
                    <div className="text-[13.5px] font-semibold tracking-tight text-slate-900">{f.country}</div>
                    <div className="text-[11px] text-slate-500">{f.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Made in Korea band ──────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-24 md:mt-32 rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-[#FAF5FF] to-[#F0F9FF] p-8 md:p-12"
        >
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <span className="label-uppercase text-slate-400">Made in Korea</span>
              <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight mt-2 leading-tight">
                <span className="font-serif italic">Engineered</span> at the source of K-beauty.
              </h3>
              <p className="mt-5 text-[14px] md:text-[15px] text-slate-700 leading-[1.8] max-w-2xl">
                Every product is developed and manufactured in South Korea, leveraging the
                country's globally recognized strengths in beauty innovation, advanced
                formulations, wellness technology, and premium manufacturing standards.
              </p>
            </div>
            <div className="md:col-span-5 grid grid-cols-2 gap-3">
              {["Beauty Innovation", "Advanced Formulations", "Wellness Tech", "Premium Mfg."].map((s) => (
                <div key={s} className="rounded-xl bg-white border border-slate-200 px-4 py-3 text-[12.5px] font-medium text-slate-800 text-center">
                  {s}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Approach ────────────────────────────────────────────────────────── */}
        <div className="mt-24 md:mt-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <span className="label-uppercase text-[#A78BFA]">Our Approach</span>
              <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight mt-2 leading-tight">
                More than trade. <span className="font-serif italic">A modern lifestyle business.</span>
              </h3>
            </div>
            <p className="md:max-w-sm text-[13px] text-slate-500 leading-relaxed">
              Five principles that shape how we build, brand and scale.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4" data-testid="about-approach">
            {APPROACH.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 transition-colors h-full flex flex-col"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-900 text-white">
                  <a.Icon size={18} weight="regular" />
                </span>
                <div className="mt-4 font-display text-[15px] font-semibold tracking-tight text-slate-900">
                  {a.title}
                </div>
                <p className="mt-2 text-[13px] text-slate-500 leading-relaxed">{a.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Vision ──────────────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-24 md:mt-32 relative rounded-3xl overflow-hidden bg-[#0F172A] text-white p-10 md:p-16"
          data-testid="about-vision"
        >
          <div className="absolute inset-0 opacity-40 pointer-events-none">
            <div className="absolute -top-20 -left-10 w-96 h-96 bg-[#FF6B9E] rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -right-10 w-96 h-96 bg-[#A78BFA] rounded-full blur-3xl" />
            <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-[#38BDF8] rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl">
            <span className="label-uppercase text-white/60">Our Vision</span>
            <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-light tracking-[-0.02em] mt-4 leading-[1.1]">
              To introduce the{" "}
              <span className="font-serif italic font-normal">future</span> of Korean
              lifestyle culture to the world —
            </h3>
            <p className="mt-5 text-[15px] md:text-[17px] text-white/75 leading-[1.8] max-w-3xl">
              through innovative products that combine wellness, functionality, aesthetics,
              and modern consumer identity.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
